/**
 * Property data validation and normalization utilities
 * Ensures properties have the required structure to prevent runtime errors
 */

/**
 * Validates and normalizes property metadata
 * @param {Object} property - The property object to validate
 * @returns {Object} - Normalized property with safe defaults
 */
export const validatePropertyData = (property) => {
  if (!property) {
    return {
      metaData: {
        images: [],
        amenities: {},
        essentials: {},
        buildingName: 'Unknown Property',
        description: '',
        location: { lat: 0, lng: 0 },
        addressString: '',
        wifiPass: '',
        cancellation: [],
        pets: false,
        propertyType: 0
      },
      short: {
        price_per_day: 0,
        islisted: false,
        auto_approve: false
      },
      access: {
        owner: ''
      },
      rentals: {
        rentals: []
      },
      token_id: ''
    };
  }

  return {
    ...property,
    metaData: {
      images: Array.isArray(property.metaData?.images) ? property.metaData.images : [],
      amenities: property.metaData?.amenities || {},
      essentials: property.metaData?.essentials || {},
      buildingName: property.metaData?.buildingName || 'Unknown Property',
      description: property.metaData?.description || '',
      location: property.metaData?.location || { lat: 0, lng: 0 },
      addressString: property.metaData?.addressString || '',
      wifiPass: property.metaData?.wifiPass || '',
      cancellation: Array.isArray(property.metaData?.cancellation) ? property.metaData.cancellation : [],
      pets: Boolean(property.metaData?.pets),
      propertyType: property.metaData?.propertyType || 0,
      ...property.metaData
    },
    short: {
      price_per_day: Number(property.short?.price_per_day) || 0,
      islisted: Boolean(property.short?.islisted),
      auto_approve: Boolean(property.short?.auto_approve),
      ...property.short
    },
    access: {
      owner: property.access?.owner || '',
      ...property.access
    },
    rentals: {
      rentals: Array.isArray(property.rentals?.rentals) ? property.rentals.rentals : [],
      ...property.rentals
    },
    token_id: property.token_id || ''
  };
};

/**
 * Checks if a property has valid image data
 * @param {Object} property - The property to check
 * @returns {boolean} - True if property has valid images
 */
export const hasValidImages = (property) => {
  return property?.metaData?.images && 
         Array.isArray(property.metaData.images) && 
         property.metaData.images.length > 0 &&
         property.metaData.images.some(img => img && img.trim() !== '');
};

/**
 * Gets a safe image URL with fallback
 * @param {Object} property - The property object
 * @param {number} index - Image index (default: 0)
 * @returns {string} - Safe image URL or placeholder
 */
export const getSafeImageUrl = (property, index = 0) => {
  const images = property?.metaData?.images;
  if (images && Array.isArray(images) && images[index]) {
    return images[index];
  }
  return '/placeholder.png';
};

/**
 * Gets safe amenities data
 * @param {Object} property - The property object
 * @returns {Object} - Safe amenities object
 */
export const getSafeAmenities = (property) => {
  return property?.metaData?.amenities || {};
};

/**
 * Checks if property has amenities
 * @param {Object} property - The property to check
 * @returns {boolean} - True if property has amenities
 */
export const hasAmenities = (property) => {
  const amenities = getSafeAmenities(property);
  return Object.keys(amenities).length > 0 && 
         Object.values(amenities).some(arr => Array.isArray(arr) && arr.length > 0);
};

/**
 * Returns price_per_day as-is from backend (backend already has correct format)
 * Backend provides prices in display format (e.g., 5 for $5, 70 for $70)
 * Do NOT convert - just return the raw backend value
 * @param {number|string} price - The price from backend
 * @returns {number} - Price as-is from backend
 */
export const normalizePricePerDay = (price) => {
  if (!price || price === 0) return 0;
  
  const priceNum = Number(price);
  if (isNaN(priceNum)) return 0;
  
  // Backend already provides prices in correct format
  // Just return the value as-is - no conversion needed
  return priceNum;
};

/**
 * Validates if a property has valid location coordinates
 * @param {Object} property - The property object
 * @returns {boolean} - True if property has valid location coordinates
 */
export const hasValidLocation = (property) => {
  const location = property?.metaData?.location;
  if (!location) return false;
  
  const lat = typeof location.lat === 'number' ? location.lat : parseFloat(location.lat);
  const lng = typeof location.lng === 'number' ? location.lng : parseFloat(location.lng);
  
  // Check if both are valid numbers and not zero (valid coordinates)
  return (
    !isNaN(lat) && 
    !isNaN(lng) && 
    lat !== 0 && 
    lng !== 0 && 
    lat >= -90 && 
    lat <= 90 && 
    lng >= -180 && 
    lng <= 180
  );
};

/**
 * Gets safe location coordinates with validation
 * @param {Object} property - The property object
 * @returns {Object|null} - Safe location object with lat/lng or null if invalid
 */
export const getSafeLocation = (property) => {
  if (!hasValidLocation(property)) return null;
  
  const location = property.metaData.location;
  const lat = typeof location.lat === 'number' ? location.lat : parseFloat(location.lat);
  const lng = typeof location.lng === 'number' ? location.lng : parseFloat(location.lng);
  
  return { lat, lng };
};